import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Button,
  Grid,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import WarningIcon from '@material-ui/icons/Warning';
import { firestore } from '../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  warningIcon: {
    fontSize: 60,
    color: theme.palette.warning.main,
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  warningBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(3),
  },
}));

const UserDeactivatePage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchUser();
  }, [id]);

  const fetchUser = async () => {
    try {
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const userDoc = doc(firestore, 'users', id);
      const userSnapshot = await getDoc(userDoc);
      
      if (userSnapshot.exists()) {
        setUser({
          id: userSnapshot.id,
          ...userSnapshot.data()
        });
      } else {
        // Si l'utilisateur n'existe pas, utiliser des données de démonstration
        setUser({
          id: id,
          name: 'John Doe',
          email: 'john.doe@example.com',
          role: 'Admin',
          status: 'Active',
        });
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données de l\'utilisateur',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async () => {
    setOpenConfirmDialog(true);
  };

  const handleConfirmDeactivate = async () => {
    setOpenConfirmDialog(false);
    setLoading(true);
    
    try {
      // Simulation de mise à jour dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const userDoc = doc(firestore, 'users', id);
      await updateDoc(userDoc, {
        status: 'Inactive',
        deactivatedAt: new Date(),
        deactivatedReason: 'Désactivé par l\'administrateur'
      });
      
      setSnackbar({
        open: true,
        message: 'Utilisateur désactivé avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la liste après un court délai
      setTimeout(() => {
        navigate('/admin/users');
      }, 1500);
      
    } catch (error) {
      console.error('Error deactivating user:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la désactivation de l\'utilisateur',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const handleCancelDeactivate = () => {
    setOpenConfirmDialog(false);
  };

  const handleBack = () => {
    navigate('/admin/users');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        style={{ marginBottom: 16 }}
      >
        Retour à la liste
      </Button>
      
      <Typography variant="h4" component="h1" className={classes.title}>
        Désactiver un utilisateur
      </Typography>
      
      {loading ? (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      ) : user ? (
        <Paper className={classes.paper}>
          <Box className={classes.warningBox}>
            <WarningIcon className={classes.warningIcon} />
            <Typography variant="h5" gutterBottom>
              Vous êtes sur le point de désactiver l'utilisateur suivant :
            </Typography>
            <Typography variant="h6" gutterBottom>
              {user.name}
            </Typography>
            <Typography variant="body1" paragraph>
              Email: {user.email}
            </Typography>
            <Typography variant="body1" paragraph>
              Rôle: {user.role}
            </Typography>
            <Typography variant="body1" paragraph>
              Statut actuel: {user.status}
            </Typography>
            
            <Typography variant="body1" paragraph style={{ marginTop: 16 }}>
              La désactivation d'un utilisateur l'empêchera de se connecter à l'application et d'utiliser ses fonctionnalités.
              Cette action peut être annulée ultérieurement en modifiant le statut de l'utilisateur.
            </Typography>
            
            <Grid container spacing={2} justifyContent="center" style={{ marginTop: 16 }}>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleBack}
                >
                  Annuler
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleDeactivate}
                >
                  Désactiver l'utilisateur
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      ) : (
        <Paper className={classes.paper}>
          <Typography variant="body1">
            Utilisateur non trouvé
          </Typography>
        </Paper>
      )}
      
      {/* Dialog de confirmation */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCancelDeactivate}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmer la désactivation"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Êtes-vous sûr de vouloir désactiver l'utilisateur {user?.name} ? 
            L'utilisateur ne pourra plus se connecter à l'application.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeactivate} color="primary">
            Annuler
          </Button>
          <Button onClick={handleConfirmDeactivate} color="secondary" autoFocus>
            Confirmer la désactivation
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserDeactivatePage;
